import { useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { GraduationCap, X, Check, Sparkles, Baby, Rocket } from "lucide-react";

// Reading-level / skill badge shown on video cards. Clickable → welcoming explainer lightbox.
const LEVELS = {
  everyone: { label: "All Levels", chip: "All Levels", bg: "#eef3ea", fg: "#4f7746", ring: "#cfe0c6", dfg: "#8fbf82",
    blurb: "Written to welcome everyone — clear, friendly and easy to follow, whatever your background." },
  kids: { label: "Playful", chip: "Playful", bg: "#fdf3e0", fg: "#b45309", ring: "#f0d190", dfg: "#e0a92e",
    blurb: "Short, warm sentences and everyday words — lovely for younger readers, or anyone who simply likes it kept simple." },
  teens: { label: "Everyday", chip: "Everyday", bg: "#e7effd", fg: "#2563eb", ring: "#c9dbff", dfg: "#7aa7ff",
    blurb: "Clear, lively language with a little more depth — great for growing readers and curious beginners alike." },
  adults: { label: "In-Depth", chip: "In-Depth", bg: "#e2f0ee", fg: "#0f766e", ring: "#b8ddd7", dfg: "#4fd1c5",
    blurb: "Richer vocabulary, deeper context and nuance — for anyone who wants the full, unhurried treatment." },
};
const ORDER = ["everyone", "kids", "teens", "adults"];

export const AudienceBadge = ({ audience = "everyone", compact = false, className = "", topic = "", dark = false }) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const key = LEVELS[(audience || "everyone").toLowerCase()] ? (audience || "everyone").toLowerCase() : "everyone";
  const lvl = LEVELS[key];

  const makeVersion = (level) => {
    const q = new URLSearchParams();
    if (topic) q.set("goal", topic);
    q.set("audience", level);
    try { localStorage.setItem("sms_studio_prefill", JSON.stringify({ goal: topic || "", audience: level })); } catch { /* ignore */ }
    navigate(`/studio?${q.toString()}`);
  };

  return (
    <>
      <button
        type="button"
        data-testid={`audience-badge-${key}`}
        onClick={(e) => { e.stopPropagation(); e.preventDefault(); setOpen(true); }}
        title={`Reading level: ${lvl.label} — tap to learn more`}
        className={`inline-flex items-center gap-1 font-bold rounded-full leading-none transition-transform active:scale-95 ${compact ? "text-[10px] px-1.5 py-0.5" : "text-[11px] px-2 py-1"} ${className}`}
        style={dark
          ? { background: "transparent", color: lvl.dfg, boxShadow: `inset 0 0 0 1.5px ${lvl.dfg}` }
          : { background: lvl.bg, color: lvl.fg, boxShadow: `inset 0 0 0 1.5px ${lvl.ring}` }}
      >
        <GraduationCap size={compact ? 11 : 13} /> {compact ? lvl.chip : lvl.label}
      </button>

      {open && createPortal(
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto"
          onClick={(e) => { e.stopPropagation(); setOpen(false); }} data-testid="audience-info-modal">
          <div className="bg-white rounded-3xl w-full max-w-md shadow-2xl overflow-hidden border border-[#dfe7d8] my-auto max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="relative px-6 py-5 text-white shrink-0" style={{ background: "linear-gradient(135deg,#5d8a52,#3f6238)" }}>
              <button data-testid="audience-info-close" onClick={() => setOpen(false)} className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-white/15 transition-colors"><X size={20} /></button>
              <div className="flex items-center gap-2.5">
                <span className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center shrink-0"><GraduationCap size={22} /></span>
                <div>
                  <h3 className="text-lg font-extrabold leading-tight">Reading Levels</h3>
                  <p className="text-[13px] font-semibold text-white/85 leading-tight">Learning that meets you where you are</p>
                </div>
              </div>
            </div>

            <div className="px-5 py-4 space-y-2 overflow-y-auto">
              <p className="text-sm text-[#5b5872] px-1 pb-1 leading-relaxed">
                Every video is written at a chosen <b className="text-[#3f6238]">reading level</b> — it's about the <i>words and depth</i>, never a label on you. Some kids devour the deepest reads; plenty of grown-ups love things kept beautifully simple. There's no wrong choice here — pick whatever feels good, and switch anytime. This one is set to:
              </p>
              {ORDER.map((k) => {
                const L = LEVELS[k];
                const active = k === key;
                return (
                  <div key={k} className={`flex items-start gap-2.5 rounded-2xl px-3 py-2.5 border-2 ${active ? "" : "border-transparent"}`}
                    style={active ? { background: L.bg, borderColor: L.ring } : { background: "#f6f7f4" }}>
                    <span className="mt-0.5 w-7 h-7 rounded-full flex items-center justify-center shrink-0 font-extrabold" style={{ background: L.bg, color: L.fg }}>
                      {active ? <Check size={15} /> : <GraduationCap size={14} />}
                    </span>
                    <div>
                      <div className="font-bold text-sm text-[#2c2841] flex items-center gap-2">
                        {L.label}{active && <span className="text-[10px] font-extrabold uppercase tracking-wide" style={{ color: L.fg }}>This video</span>}
                      </div>
                      <div className="text-[12.5px] text-[#6b6880] leading-snug">{L.blurb}</div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="px-5 pb-5 pt-1 shrink-0">
              <div className="rounded-2xl bg-[#eef3ea] border border-[#cfe0c6] p-3.5">
                <div className="flex items-center gap-1.5 text-sm font-extrabold text-[#2c2841]">
                  <Sparkles size={15} className="text-[#4f7746]" /> Grow your skills with our community
                </div>
                <p className="text-[12.5px] text-[#5b5872] mt-0.5 mb-2.5 leading-snug">
                  Want {topic ? <b>“{topic.length > 40 ? topic.slice(0, 40) + "…" : topic}”</b> : "this topic"} in your voice? Make your own version — a little simpler or a little deeper — tuned exactly to how you learn best.
                </p>
                <div className="flex flex-wrap gap-2">
                  <button
                    data-testid="audience-make-easier"
                    onClick={() => makeVersion("kids")}
                    className="inline-flex items-center gap-1.5 text-sm font-bold text-[#b45309] bg-[#fdf3e0] border-2 border-[#f0d190] px-3 py-1.5 rounded-full hover:bg-[#fbe9cf] transition-colors"
                  >
                    <Baby size={15} /> Make it simpler
                  </button>
                  <button
                    data-testid="audience-make-pro"
                    onClick={() => makeVersion("adults")}
                    className="inline-flex items-center gap-1.5 text-sm font-bold text-white px-3 py-1.5 rounded-full transition-transform hover:scale-105"
                    style={{ background: "linear-gradient(135deg,#5d8a52,#3f6238)" }}
                  >
                    <Rocket size={15} /> Make it deeper
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default AudienceBadge;
